import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { dummyP2POrders } from "../../../data/dummyData";
import "../Home/Homepage.css";
import "./P2POrdersPage.css";

const P2POrdersPage = () => {
  const [orders] = useState(dummyP2POrders);
  const [filters, setFilters] = useState({
    status: "All",
    side: "",
    fiatCurrency: "",
    quoteCurrency: "",
    buyerId: "",
    sellerId: "",
    adId: "",
    orderId: "",
    minAmount: "",
    maxAmount: "",
    disputeBy: "",
    dateFrom: "",
    dateTo: "",
  });

  const updateFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const resetFilters = () => {
    setFilters({
      status: "All",
      side: "",
      fiatCurrency: "",
      quoteCurrency: "",
      buyerId: "",
      sellerId: "",
      adId: "",
      orderId: "",
      minAmount: "",
      maxAmount: "",
      disputeBy: "",
      dateFrom: "",
      dateTo: "",
    });
  };

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      if (filters.status !== "All" && (o.status || "") !== filters.status) return false;
      if (filters.side && (o.side || "").toLowerCase() !== filters.side.toLowerCase()) return false;
      if (filters.fiatCurrency && (o.fiat || "").toLowerCase().indexOf(filters.fiatCurrency.toLowerCase()) === -1) return false;
      if (filters.quoteCurrency && (o.quote || "").toLowerCase().indexOf(filters.quoteCurrency.toLowerCase()) === -1) return false;
      if (filters.buyerId && (o.buyer || "").toLowerCase().indexOf(filters.buyerId.toLowerCase()) === -1) return false;
      if (filters.adId && (o.ad_uid || "").toLowerCase().indexOf(filters.adId.toLowerCase()) === -1) return false;
      if (filters.orderId && (o.order_id || "").toLowerCase().indexOf(filters.orderId.toLowerCase()) === -1) return false;
      if (filters.minAmount !== "" && Number(o.fiat_amount) < Number(filters.minAmount)) return false;
      if (filters.maxAmount !== "" && Number(o.fiat_amount) > Number(filters.maxAmount)) return false;
      return true;
    });
  }, [orders, filters]);

  const counts = useMemo(() => ({
    total: orders.length,
    released: orders.filter((o) => (o.status || "").toLowerCase() === "released").length,
    pending: orders.filter((o) => (o.status || "").toLowerCase() === "pending").length,
    disputes: orders.filter((o) => (o.status || "").toLowerCase() === "disputes").length,
  }), [orders]);

  return (
    <main className="dashboard-main dashboard-subpage p2p-orders-page">
      <section className="dashboard-hero p2p-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>P2P Orders</span>
          </p>
          <h1 className="dashboard-hero__title">P2P Orders Management</h1>
          <p className="dashboard-hero__subtitle">Monitor and manage all P2P trading orders</p>
        </div>
        <div className="p2p-hero__icon">
          <i className="fas fa-shopping-cart" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="p2p-stats">
          <div className="p2p-stat-card">
            <i className="fas fa-list p2p-stat-card__icon p2p-stat-card__icon--default" />
            <span className="p2p-stat-card__value">{counts.total}</span>
            <span className="p2p-stat-card__label">Total Orders</span>
          </div>
          <div className="p2p-stat-card">
            <i className="fas fa-check-circle p2p-stat-card__icon p2p-stat-card__icon--released" />
            <span className="p2p-stat-card__value">{counts.released}</span>
            <span className="p2p-stat-card__label">Released</span>
          </div>
          <div className="p2p-stat-card">
            <i className="fas fa-clock p2p-stat-card__icon p2p-stat-card__icon--pending" />
            <span className="p2p-stat-card__value">{counts.pending}</span>
            <span className="p2p-stat-card__label">Pending</span>
          </div>
          <div className="p2p-stat-card">
            <i className="fas fa-exclamation-triangle p2p-stat-card__icon p2p-stat-card__icon--disputes" />
            <span className="p2p-stat-card__value">{counts.disputes}</span>
            <span className="p2p-stat-card__label">Disputes</span>
          </div>
        </div>

        <section className="p2p-filters-section">
          <h2 className="p2p-filters__title"><i className="fas fa-filter" /> Filters</h2>
          <div className="p2p-filters-grid">
            <div className="p2p-filter-group">
              <label>Status</label>
              <select value={filters.status} onChange={(e) => updateFilter("status", e.target.value)}>
                <option value="All">All</option>
                <option value="Released">Released</option>
                <option value="Pending">Pending</option>
                <option value="Disputes">Disputes</option>
              </select>
            </div>
            <div className="p2p-filter-group">
              <label>Side</label>
              <input type="text" placeholder="e.g. BUY" value={filters.side} onChange={(e) => updateFilter("side", e.target.value)} />
            </div>
            <div className="p2p-filter-group">
              <label>Fiat Currency</label>
              <input type="text" placeholder="e.g., USD" value={filters.fiatCurrency} onChange={(e) => updateFilter("fiatCurrency", e.target.value)} />
            </div>
            <div className="p2p-filter-group">
              <label>Quote Currency</label>
              <input type="text" placeholder="e.g., BTC" value={filters.quoteCurrency} onChange={(e) => updateFilter("quoteCurrency", e.target.value)} />
            </div>
            <div className="p2p-filter-group">
              <label>Buyer ID</label>
              <input type="text" placeholder="Buyer ID" value={filters.buyerId} onChange={(e) => updateFilter("buyerId", e.target.value)} />
            </div>
            <div className="p2p-filter-group">
              <label>Seller ID</label>
              <input type="text" placeholder="Seller ID" value={filters.sellerId} onChange={(e) => updateFilter("sellerId", e.target.value)} />
            </div>
            <div className="p2p-filter-group">
              <label>Ad ID</label>
              <input type="text" placeholder="Ad ID" value={filters.adId} onChange={(e) => updateFilter("adId", e.target.value)} />
            </div>
            <div className="p2p-filter-group">
              <label>Order ID</label>
              <input type="text" placeholder="Order ID" value={filters.orderId} onChange={(e) => updateFilter("orderId", e.target.value)} />
            </div>
            <div className="p2p-filter-group">
              <label>Min Amount</label>
              <input type="text" placeholder="Min Amount" value={filters.minAmount} onChange={(e) => updateFilter("minAmount", e.target.value)} />
            </div>
            <div className="p2p-filter-group">
              <label>Max Amount</label>
              <input type="text" placeholder="Max Amount" value={filters.maxAmount} onChange={(e) => updateFilter("maxAmount", e.target.value)} />
            </div>
            <div className="p2p-filter-group">
              <label>Dispute By</label>
              <input type="text" value={filters.disputeBy} onChange={(e) => updateFilter("disputeBy", e.target.value)} />
            </div>
            <div className="p2p-filter-group">
              <label>Date From</label>
              <input type="date" placeholder="dd-mm-yyyy" value={filters.dateFrom} onChange={(e) => updateFilter("dateFrom", e.target.value)} />
            </div>
            <div className="p2p-filter-group">
              <label>Date To</label>
              <input type="date" placeholder="dd-mm-yyyy" value={filters.dateTo} onChange={(e) => updateFilter("dateTo", e.target.value)} />
            </div>
          </div>
          <div className="p2p-filters-actions">
            <button type="button" className="p2p-btn-reset" onClick={resetFilters}>
              <i className="fas fa-sync-alt" /> Reset Filters
            </button>
          </div>
        </section>

        <section className="p2p-orders-section">
          <h2 className="p2p-orders__title"><i className="fas fa-th-list" /> Orders List ({filteredOrders.length})</h2>
          <div className="p2p-table-wrap">
            <table className="p2p-table">
              <thead>
                <tr>
                  <th>SR NO.</th>
                  <th>ORDER ID</th>
                  <th>AD UID</th>
                  <th>SIDE</th>
                  <th>FIAT</th>
                  <th>QUOTE</th>
                  <th>FIAT AMOUNT</th>
                  <th>CRYPTO AMOUNT</th>
                  <th>PRICE</th>
                  <th>BUYER</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((row) => (
                  <tr key={row.sr}>
                    <td>{row.sr}</td>
                    <td>{row.order_id}</td>
                    <td>{row.ad_uid}</td>
                    <td><span className={`p2p-side-badge p2p-side-badge--${(row.side || "").toLowerCase()}`}>{row.side}</span></td>
                    <td>{row.fiat}</td>
                    <td>{row.quote}</td>
                    <td>{row.fiat_amount}</td>
                    <td>{row.crypto_amount}</td>
                    <td>{row.price}</td>
                    <td>{row.buyer}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </main>
  );
};

export default P2POrdersPage;
